import { useMemo } from "react";

import { Dropdown } from "@magnetic/dropdown";
import { Heading } from "@magnetic/heading";
import { DotsThree, Plus } from "@magnetic/icons";

import { HFlex, PageContent, VFlex } from "src/components";
import { Button } from "src/components/buttons";
import { EmptyContainer } from "src/components/emptyContainer";
import { ErrorNotification } from "src/components/errorNotification";
import { SearchWithResultCount } from "src/components/SearchWithResultCount/SearchWithResultCount";
import { RowActions } from "src/components/tables/columnConfigs/actionsColumn";
import Table, { TableData } from "src/components/typedTable";
import { ApiError } from "src/core";
import { LinkedAccount } from "src/gen/schema/bilge/api";
import { AdminState } from "src/gen/schema/models/global";
import { useClientFilter } from "src/hooks/useClientFilter";

import {
  getPlatformLabel,
  getServiceDetails,
  getServiceDetailsTooltip
} from "./botUtils";
import { bindingsTableColumns } from "./tableColumns";

interface BotBindingsTableProps {
  readonly bindings: LinkedAccount[];
  readonly loading: boolean;
  readonly error: ApiError | undefined;
  readonly onBindBot: () => void;
  readonly onToggleBindingStatus: (
    accountId: string,
    currentAdminState: number
  ) => void;
  readonly onDeleteBinding: (binding: LinkedAccount) => void;
}

/**
 * Table component for displaying and managing bot bindings
 */
export const BotBindingsTable = ({
  bindings,
  loading,
  error,
  onBindBot,
  onToggleBindingStatus,
  onDeleteBinding
}: BotBindingsTableProps) => {
  const {
    query,
    filtered: filteredBindings,
    onSearchChange
  } = useClientFilter(bindings, (binding) => binding.userName || "");

  const bindingsTableData: TableData<typeof bindingsTableColumns>[] =
    useMemo(() => {
      if (!filteredBindings.length) {
        return [];
      }
      return filteredBindings.map((binding) => {
        const { userName, adminState, chatPlatform } = binding;
        const isEnabled = adminState === AdminState.ENABLED;
        const dropdownMenu = (
          <Dropdown
            key={binding.accountId}
            icon={<DotsThree />}
            kind="tertiary"
          >
            <Dropdown.Item
              destructive={false}
              label={`${isEnabled ? "Disable" : "Enable"}`}
              onClick={(e) => {
                e.stopPropagation();
                onToggleBindingStatus(binding.accountId, binding.adminState);
              }}
              data-qa="toggle-bot-binding"
            />
            <Dropdown.Item
              destructive={true}
              label="Delete bot binding"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteBinding(binding);
              }}
              data-qa="delete-bot-binding"
            />
          </Dropdown>
        );

        const actions: RowActions = [
          {
            customComponent: dropdownMenu
          }
        ];

        return {
          service: getPlatformLabel(chatPlatform),
          botUser: userName,
          bindStatus: isEnabled,
          addedBy: "", // TODO: Add creator info when available
          addedDate: undefined, // TODO: Add created date when available
          serviceDetails: {
            displayData: getServiceDetails(binding),
            tooltipData: getServiceDetailsTooltip(binding)
          },
          action: actions
        };
      });
    }, [filteredBindings, onDeleteBinding, onToggleBindingStatus]);

  return (
    <PageContent
      classNames={["bot-table-container"]}
      titleSection={
        <VFlex>
          <ErrorNotification error={error} />
          <HFlex gap="md">
            <Heading>Bot bindings</Heading>
          </HFlex>
          <HFlex align="center" justify="space-between">
            <SearchWithResultCount
              searchTerm={query}
              onSearchChange={onSearchChange}
              placeholder="Search by bot user"
              resultCount={filteredBindings.length}
              dataQa="search-bot-bindings"
            />
            <Button
              icon={<Plus />}
              name="bind-bot-user"
              onClick={onBindBot}
              kind="primary"
            >
              Bind bot user
            </Button>
          </HFlex>
        </VFlex>
      }
      emptyState={
        !loading &&
        bindings.length === 0 && (
          <EmptyContainer
            text="Link a bot user to a messaging platform chat room."
            title="Bind bot user"
            fontSize="p2"
            iconName="bind-bot"
          />
        )
      }
      loading={loading}
    >
      {bindings.length > 0 && (
        <div className="table-scroll-container">
          <Table
            name="bot-bindings-table"
            className="bot-bindings-table"
            columns={bindingsTableColumns}
            data={bindingsTableData}
            stickyHeader={true}
            size="compact"
          />
        </div>
      )}
    </PageContent>
  );
};
